var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, createRef } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { isNil } from '../../util/type-check';
import { throttle } from '../../util/helpers';
import TableHead from './components/TableHead';
import TableBody from './components/TableBody';
import { defaultSortBy, getSortDirection } from './utils';
const tableContainerBaseStyles = () => css `
  position: relative;
`;
const tableContainerScrollableStyles = ({ scrollable, }) => scrollable &&
    css `
    height: 100%;
  `;
const shadowStyles = ({ theme, noShadow, }) => !noShadow &&
    css `
    border: ${theme.borderWidth.kilo} solid var(--cui-border-divider);
  `;
const TableContainer = styled.div(tableContainerBaseStyles, tableContainerScrollableStyles, shadowStyles);
const containerStyles = ({ theme, rowHeaders, }) => rowHeaders &&
    css `
    border-radius: ${theme.borderRadius.bit};
    ${theme.mq.untilMega} {
      height: unset;
      overflow-x: auto;
    }
  `;
const scrollableStyles = ({ scrollable, height }) => scrollable &&
    css `
    height: ${height || '100%'};
    overflow-y: auto;
  `;
const ScrollContainer = styled.div(containerStyles, scrollableStyles);
const baseStyles = css `
  background-color: var(--cui-bg-normal);
  border-collapse: separate;
  width: 100%;
`;
const borderCollapsedStyles = ({ borderCollapsed }) => borderCollapsed &&
    css `
    border-collapse: collapse;
  `;
const StyledTable = styled.table(baseStyles, borderCollapsedStyles);
/**
 * Table interface component. It handles rendering rows/headers properly
 */
class Table extends Component {
    constructor(props) {
        super(props);
        this.tableRef = createRef();
        this.addVerticalScroll = () => {
            this.calculateTableBodyHeight();
            window.addEventListener('resize', throttle(this.calculateTableBodyHeight, 1000));
        };
        this.removeVerticalScroll = () => {
            window.removeEventListener('resize', this.calculateTableBodyHeight);
        };
        this.calculateTableBodyHeight = () => {
            this.setState({
                tableBodyHeight: isNil(this.tableRef.current) ||
                    isNil(this.tableRef.current.parentElement)
                    ? 'unset'
                    : `${this.tableRef.current.parentElement.offsetHeight}px`,
            });
        };
        this.onSortEnter = (i) => this.setState({ sortHover: i });
        this.onSortLeave = () => this.setState({ sortHover: undefined });
        this.onSortBy = (i) => {
            const { sortedRow, sortDirection } = this.state;
            const isActive = i === sortedRow;
            const nextDirection = getSortDirection(isActive, sortDirection);
            const sortedRows = this.getSortedRows(nextDirection, i);
            this.updateSort(i, nextDirection, sortedRows);
        };
        this.getInitialRows = (rows, initialSortDirection, initialSortedRow) => {
            if (initialSortedRow && initialSortDirection) {
                return this.getSortedRows(initialSortDirection, initialSortedRow);
            }
            return rows;
        };
        this.getSortedRows = (sortDirection, sortedRow) => {
            const { rows, onSortBy } = this.props;
            return onSortBy
                ? onSortBy(sortedRow, rows, sortDirection)
                : defaultSortBy(sortedRow, rows, sortDirection);
        };
        this.updateSort = (i, nextDirection, sortedRows) => this.setState({
            sortedRow: i,
            sortDirection: nextDirection,
            rows: sortedRows,
        });
        this.handleScroll = (e) => {
            this.setState({ scrollTop: e.currentTarget.scrollTop });
        };
        this.state = {
            sortedRow: this.props.initialSortedRow,
            rows: this.getInitialRows(this.props.rows, this.props.initialSortDirection, this.props.initialSortedRow),
            sortHover: undefined,
            sortDirection: this.props.initialSortDirection,
            scrollTop: undefined,
            tableBodyHeight: undefined,
        };
    }
    componentDidMount() {
        if (this.props.scrollable) {
            this.addVerticalScroll();
        }
    }
    componentDidUpdate(prevProps) {
        if (this.props.rows !== prevProps.rows) {
            // Preserve existing sorting
            if (this.state.sortedRow && this.state.sortDirection) {
                const sortedRows = this.getSortedRows(this.state.sortDirection, this.state.sortedRow);
                this.setState({ rows: sortedRows });
                return;
            }
            this.setState({ rows: this.props.rows });
        }
        if (!prevProps.scrollable && this.props.scrollable) {
            this.addVerticalScroll();
        }
        if (prevProps.scrollable && !this.props.scrollable) {
            this.removeVerticalScroll();
        }
    }
    componentWillUnmount() {
        if (this.props.scrollable) {
            this.removeVerticalScroll();
        }
    }
    render() {
        const _a = this.props, { rowHeaders = true, headers = [], noShadow = false, borderCollapsed = false, condensed = false, scrollable = false, onRowClick, rows: initialRows, onSortBy } = _a, props = __rest(_a, ["rowHeaders", "headers", "noShadow", "borderCollapsed", "condensed", "scrollable", "onRowClick", "rows", "onSortBy"]);
        const { sortDirection, sortHover, sortedRow, scrollTop, tableBodyHeight, rows, } = this.state;
        return (_jsx(TableContainer, Object.assign({ ref: this.tableRef, scrollable: scrollable, noShadow: noShadow }, props, { children: _jsx(ScrollContainer, Object.assign({ rowHeaders: rowHeaders, scrollable: scrollable, height: tableBodyHeight, onScroll: scrollable ? this.handleScroll : undefined }, { children: _jsxs(StyledTable, Object.assign({ rowHeaders: rowHeaders, borderCollapsed: borderCollapsed }, { children: [_jsx(TableHead, { top: scrollTop, condensed: condensed, scrollable: scrollable, sortDirection: sortDirection, sortedRow: sortedRow, onSortBy: this.onSortBy, onSortEnter: this.onSortEnter, onSortLeave: this.onSortLeave, headers: headers, rowHeaders: rowHeaders }), _jsx(TableBody, { condensed: condensed, rows: rows, rowHeaders: rowHeaders, sortHover: sortHover, onRowClick: onRowClick })] })) })) })));
    }
}
export default Table;
